import {
  DownOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Space, ConfigProvider, theme } from 'antd';
import type { FormInstance } from 'antd/es/form';
import type { CSSProperties, ReactNode } from 'react';
import { useMemo, useState } from 'react';

const { useToken } = theme;

export interface CollapsibleFilterItem {
  key: string;
  label?: ReactNode;
  node: ReactNode;
  // 可以根据需要传入自定义样式，比如宽度
  style?: CSSProperties;
}

interface CollapsibleFilterFormProps<T extends object> {
  form: FormInstance<T>;
  items: CollapsibleFilterItem[];
  defaultVisibleCount?: number;
  onFinish: (values: T) => void;
  onReset: () => void;
  primaryAction?: ReactNode;
  submitText?: string;
  style?: CSSProperties;
}

function CollapsibleFilterForm<T extends object>({
  form,
  items,
  defaultVisibleCount = 3,
  onFinish,
  onReset,
  primaryAction,
  submitText = '查询',
  style,
}: CollapsibleFilterFormProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const { token } = useToken();

  const shouldCollapse = items.length > defaultVisibleCount;

  const primaryItems = useMemo(
    () => items.slice(0, defaultVisibleCount),
    [items, defaultVisibleCount],
  );
  const overflowItems = useMemo(
    () => items.slice(defaultVisibleCount),
    [items, defaultVisibleCount],
  );

  const toggleExpand = () => setExpanded(!expanded);

  const actionButtons = (
    <Space size={[10, 10]} wrap>
      <Button
        htmlType="submit"
        type="primary"
        icon={<SearchOutlined />}
        style={{
          borderRadius: 8,
          height: 36,
          paddingInline: 18,
          boxShadow: `0 4px 12px ${token.colorPrimary}30`,
          fontWeight: 500,
        }}
      >
        {submitText}
      </Button>
      <Button
        onClick={onReset}
        icon={<ReloadOutlined />}
        style={{
          borderRadius: 8,
          height: 36,
          paddingInline: 14,
          color: token.colorTextSecondary,
          borderColor: token.colorBorderSecondary,
        }}
      >
        重置
      </Button>
      {shouldCollapse && (
        <Button
          type="text"
          onClick={toggleExpand}
          style={{
            height: 36,
            color: token.colorPrimary,
            paddingInline: 4,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>{expanded ? '收起' : '展开'}</span>
          <span
            style={{
              display: 'flex',
              transform: `rotate(${expanded ? 180 : 0}deg)`,
              transition: 'transform 0.18s ease',
            }}
          >
            <DownOutlined style={{ fontSize: 11 }} />
          </span>
        </Button>
      )}
    </Space>
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
          Input: { controlHeight: 36, borderRadius: 8 },
          Select: { controlHeight: 36, borderRadius: 8 },
          DatePicker: { controlHeight: 36, borderRadius: 8 },
        },
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '16px 20px',
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          ...style,
        }}
      >
        <Form<T>
          form={form}
          onFinish={onFinish}
          layout="horizontal"
          autoComplete="off"
        >
          {/* 第一行：紧凑排列 */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '12px 24px',
            }}
          >
            {primaryItems.map((item) => (
              <div
                key={item.key}
                style={{
                  flex: '0 1 auto',
                  maxWidth: '100%',
                  minWidth: 0,
                  ...item.style,
                }}
              >
                <Form.Item
                  label={
                    <span
                      style={{
                        color: token.colorTextSecondary,
                        fontWeight: 500,
                        fontSize: 13,
                      }}
                    >
                      {item.label}
                    </span>
                  }
                  name={item.key as never}
                  labelCol={{ flex: '0 0 auto' }}
                  wrapperCol={{ flex: '1 1 auto' }}
                >
                  {item.node}
                </Form.Item>
              </div>
            ))}

            <div
              style={{
                marginInlineStart: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flex: '1 1 260px',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  gap: 12,
                  width: '100%',
                }}
              >
                <div style={{ flexShrink: 0 }}>{actionButtons}</div>
                {primaryAction ? (
                  <div style={{ flexShrink: 0 }}>
                    <Space size={[10, 10]} wrap>
                      {primaryAction}
                    </Space>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {shouldCollapse ? (
            <div
              aria-hidden={!expanded}
              style={{
                contain: 'layout paint',
                display: 'grid',
                gridTemplateRows: expanded ? '1fr' : '0fr',
                marginTop: expanded ? 12 : 0,
                opacity: expanded ? 1 : 0,
                pointerEvents: expanded ? 'auto' : 'none',
                transition:
                  'grid-template-rows 0.18s ease, opacity 0.16s ease, margin-top 0.18s ease',
                willChange: 'grid-template-rows, opacity',
              }}
            >
              <div style={{ minHeight: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '12px 24px',
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: `1px dashed ${token.colorBorderSecondary}`,
                  }}
                >
                  {overflowItems.map((item) => (
                    <div
                      key={item.key}
                      style={{
                        flex: '0 1 auto',
                        maxWidth: '100%',
                        minWidth: 0,
                        ...item.style,
                      }}
                    >
                      <Form.Item
                        label={
                          <span
                            style={{
                              color: token.colorTextSecondary,
                              fontWeight: 500,
                              fontSize: 13,
                            }}
                          >
                            {item.label}
                          </span>
                        }
                        name={item.key as never}
                        labelCol={{ flex: '0 0 auto' }}
                        wrapperCol={{ flex: '1 1 auto' }}
                      >
                        {item.node}
                      </Form.Item>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </Form>
      </div>
    </ConfigProvider>
  );
}

export default CollapsibleFilterForm;
